import { useEffect } from "react";
import type { GeneratorConfig } from "@/types/generator";

/**
 * Dynamically updates document head (title, meta tags, favicon) based on generator config
 * This enables per-generator SEO customization
 */
export function useDocumentHead(config: GeneratorConfig | null) {
  useEffect(() => {
    // Keep the tab title neutral until the generator config is loaded.
    // Leave whatever the static index.html (or a previous render) set —
    // never flash "Loading..." because JS-rendering crawlers (Googlebot)
    // can snapshot that value and index it as the page title.
    if (!config) {
      return;
    }

    // Update document title
    const title = config.meta_title || config.hero_heading || `${config.display_name} Generator`;
    document.title = title;

    // Helper to update or create a meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update meta description
    if (config.meta_description) {
      setMetaTag("description", config.meta_description);
    }

    // Update keywords
    if (config.keywords && config.keywords.length > 0) {
      setMetaTag("keywords", config.keywords.join(", "));
    }

    // Update Open Graph tags
    setMetaTag("og:title", title, true);
    if (config.meta_description) {
      setMetaTag("og:description", config.meta_description, true);
    }
    if (config.og_image_path) {
      setMetaTag("og:image", config.og_image_path, true);
    }

    // Update Twitter Card tags
    setMetaTag("twitter:title", title);
    if (config.meta_description) {
      setMetaTag("twitter:description", config.meta_description);
    }
    if (config.og_image_path) {
      setMetaTag("twitter:image", config.og_image_path);
    }

    // Update favicon
    if (config.favicon_path) {
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        favicon.type = "image/png";
        document.head.appendChild(favicon);
      }
      favicon.href = config.favicon_path;
    }

    // Update author meta
    setMetaTag("author", `${config.display_name} Generator`);

    // Insert structured data (JSON-LD) if provided
    if (config.structured_data) {
      // Remove existing structured data from this generator
      const existingScript = document.querySelector('script[data-generator-jsonld]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-generator-jsonld", "true");
      script.textContent = JSON.stringify(config.structured_data);
      document.head.appendChild(script);
    }
  }, [config]);
}
